import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { listCreditRates, updateCreditRate } from "../../api/superAdminApi";
import useNotification from "../common/Notification";
import { serviceLabel, UNIT_LABELS } from "../billing/billingFormat";

const AdminCreditRatesPage = () => {
    const notify = useNotification();
    const [rows, setRows] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const list = await listCreditRates();
            setRows(list);
            setDrafts(Object.fromEntries(list.map((row) => [row.id, row.credits == null ? "" : String(row.credits)])));
        } catch (err) {
            setError(err.message || "Could not load credit rates");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (row) => {
        const raw = drafts[row.id];
        const credits = raw === "" ? null : Number(raw);
        if (credits != null && (!Number.isInteger(credits) || credits < 0)) {
            notify("Enter a whole number of credits, or leave the price blank", "error");
            return;
        }
        setSavingId(row.id);
        try {
            await updateCreditRate(row.id, credits);
            notify(
                credits == null
                    ? `${serviceLabel(row.service, row.template_category)} price cleared. Sends for this service will be blocked until a price is set.`
                    : `${serviceLabel(row.service, row.template_category)} saved at ${credits} credits`,
                "success"
            );
            load();
        } catch (err) {
            notify(err.message || "Could not save the rate", "error");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Credit rates</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Prices are in credits. 1 credit = 1 paisa. A blank price blocks that service until you set one.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
                Email AI is charged per 1,000 tokens. Every other row is charged per message.
            </Alert>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
            ) : (
                <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {["Service", "Unit", "Credits", ""].map((heading) => (
                                    <TableCell key={heading || "action"} sx={{ fontWeight: 700 }}>{heading}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{serviceLabel(row.service, row.template_category)}</TableCell>
                                    <TableCell>{UNIT_LABELS[row.unit] || row.unit || "—"}</TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={drafts[row.id] ?? ""}
                                            onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                            inputProps={{ min: 0, step: 1 }}
                                            placeholder="Not set"
                                            sx={{ width: 140 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={savingId === row.id}
                                            onClick={() => save(row)}
                                        >
                                            {savingId === row.id ? "Saving…" : "Save"}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>No rates have been created yet.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

export default AdminCreditRatesPage;
